import { useCallback, useEffect } from 'react';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'support/types/ui';
import { WithRef } from 'support/types/withRef';
import { useInterpolatedEnumerableOptions } from 'components/SchemableForm/hooks/useInterpolatedEnumerableOptions';
import { Translate } from 'components/Translate/Translate';

import { Dropdown } from '../Dropdown/Dropdown';

type MessageDropdownProps = Omit<
  Parameters<typeof Dropdown>[0],
  'errors' | 'fieldState' | 'options' | 'label' | 'multiple' | 'value' | 'onChange' | 'defaultValue'
> & {
  error: WebEDIAnnotation | undefined;
  field: ui.EnumerableField;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  value: string;
  onChange?: (value: string) => void;
  defaultValue?: string;
  fieldState: FieldState;
  variant?: 'large';
};

export const MessageDropdown = ({
  error,
  onBlur,
  field,
  onShowFieldErrorModal,
  value,
  onChange,
  defaultValue,
  fieldState,
  variant,
  ref,
  ...otherProps
}: WithRef<MessageDropdownProps, HTMLInputElement>) => {
  const { options } = useInterpolatedEnumerableOptions(field.type_config);
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const isLargeVariant = variant === 'large';

  const onDropdownFocus = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  const onDropdownBlur = useCallback(() => {
    onBlur?.();
    setShowNavigableItemPopover(false);
  }, [onBlur, setShowNavigableItemPopover]);

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, showNavigableItemPopover, onShowFieldErrorModal]);

  const inlineErrors =
    isLargeVariant && isWebEDIFormError(error) ? { message: error.description, type: error.type } : undefined;

  return (
    <div ref={isLargeVariant ? undefined : refs.setReference}>
      <Dropdown<string>
        {...otherProps}
        options={options}
        label={field.label}
        ref={ref}
        variant={variant}
        description={isLargeVariant && field.description ? <Translate i18nKey={field.description} /> : undefined}
        errors={inlineErrors}
        fieldState={fieldState}
        onFocus={isLargeVariant ? undefined : onDropdownFocus}
        onBlur={isLargeVariant ? onBlur : onDropdownBlur}
        dropdownDisplayTestId={'message-dropdown'}
        disabled={otherProps.disabled}
        multiple={false}
        value={value}
        onChange={onChange}
        defaultValue={defaultValue}
      />

      {!isLargeVariant && showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          viewMode={false}
          annotation={error}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={field.description}
        />
      )}
    </div>
  );
};
